import React from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown } from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

/**
 * Data table built on TanStack Table + design-system styling.
 * Sorting is client-side within the loaded page; pagination is caller-controlled.
 */
export const DataTable = ({
  columns,
  data,
  page = 1,
  pages = 1,
  total = 0,
  onPageChange,
  testId = "data-table",
  footer = null,
  rowTestId,
}) => {
  const [sorting, setSorting] = React.useState([]);
  const table = useReactTable({
    data: data || [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full min-w-[340px] text-xs sm:text-sm" data-testid={testId}>
          <thead className="surface-2">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b border-border">
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const dir = header.column.getIsSorted();
                  const numeric = header.column.columnDef.meta?.numeric;
                  return (
                    <th
                      key={header.id}
                      className={cn(
                        "px-2 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:px-3 sm:text-xs",
                        numeric ? "text-right" : "text-left",
                        header.column.columnDef.meta?.className
                      )}
                    >
                      {canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className={cn(
                            "inline-flex items-center gap-1.5 transition-colors hover:text-foreground",
                            numeric && "flex-row-reverse"
                          )}
                          data-testid={`sort-${header.column.id}`}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {dir === "asc" ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : dir === "desc" ? (
                            <ArrowDown className="h-3 w-3" />
                          ) : (
                            <ChevronsUpDown className="h-3 w-3 opacity-50" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, idx) => (
              <tr
                key={row.id}
                className="border-b border-border transition-colors last:border-0 hover:bg-accent/40"
                data-testid={rowTestId ? `${rowTestId}-${idx}` : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={cn(
                      "px-2 py-3 align-middle sm:px-3",
                      cell.column.columnDef.meta?.numeric && "text-right font-num",
                      cell.column.columnDef.meta?.className
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {footer}
        </table>
      </div>

      {onPageChange ? (
        <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
          <p className="text-xs text-muted-foreground" data-testid={`${testId}-count`}>
            {total === 0
              ? "No records"
              : `Showing page ${page} of ${pages} \u00B7 ${total} record${total === 1 ? "" : "s"}`}
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              data-testid="data-table-pagination-prev"
            >
              <ChevronLeft className="mr-1 h-4 w-4" /> Prev
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={page >= pages}
              onClick={() => onPageChange(page + 1)}
              data-testid="data-table-pagination-next"
            >
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
